/** Pure derivations behind the cockpit (screens §P4): everything a tile prints is computed here from contract views. */
import { CAPITAL_RECORD_COUNTS, PUBLIC_KPIS, type KpiId, type KpiSnapshot } from "@bsim/domain";
import type { ContentCatalog, MarketView, RoundReport, TeamState } from "@bsim/contracts";
import { displayRank, fiscalYearLabel, formatBp, formatKpi, printedDifference, printedKpi, quarterLabel, rateDeltaBp } from "../format";
import { kpiLabel } from "../format/labels";
import type { Headline } from "../score/headline";
import { PREMIA, PREMIUM_KEYS, type Premia } from "../valuation/premia";

type Bank = MarketView["banks"][number];

export const kpiOf = (snapshot: KpiSnapshot, id: KpiId): number | null => snapshot[id] ?? null;

/** The latest released KPIs, or SimStart before the first release (`kpiHistory` always starts at round 0). */
export function latestKpis(team: TeamState): KpiSnapshot {
  const last = team.kpiHistory[team.kpiHistory.length - 1];
  return last?.kpis ?? {};
}

/** `me` in the market = the row with the team's seat (WP13 decision). */
export const meBank = (team: TeamState, market: MarketView): Bank | undefined => market.banks.find((b) => b.seatId === team.team.seatId);

/** Values of one KPI over the released history, oldest first (a sparkline needs two or more). */
export function kpiSeries(team: TeamState, id: KpiId): number[] {
  return team.kpiHistory.flatMap((h) => { const v = h.kpis[id]; return v === undefined ? [] : [v]; });
}

const median = (values: readonly number[]): number | null => {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 === 1 ? (sorted[mid] ?? null) : ((sorted[mid - 1] ?? 0) + (sorted[mid] ?? 0)) / 2;
};

const isPublic = (id: KpiId): boolean => (PUBLIC_KPIS as readonly KpiId[]).includes(id);

/** Market median, and only for `PUBLIC_KPIS` (never a median of private KPIs). */
export function publicMedian(market: MarketView, id: KpiId): number | null {
  return isPublic(id) ? median(market.banks.flatMap((b) => { const v = b.kpis[id]; return v === undefined ? [] : [v]; })) : null;
}

/** The viewer's own limit for a KPI, from the strategy statement; given the catalog, the default limit stands in when the team has not saved one (flagged `isDefault`). */
/** The chair form was saved: every limit is then the player's own, equal to the default or not (the one rule the memo, tile and cockpit share). */
export const limitsSaved = (team: TeamState): boolean => (team.strategyStatement?.riskAppetite.length ?? 0) > 0;

export function limitFor(team: TeamState, id: KpiId, catalog?: ContentCatalog): { readonly limit: number; readonly cmp: "le" | "ge"; readonly isDefault: boolean } | null {
  const found = team.strategyStatement?.riskAppetite.find((l) => l.kpi === id);
  if (found !== undefined && limitsSaved(team)) return { limit: found.limit, cmp: found.cmp, isDefault: false };
  const fallback = catalog?.riskAppetiteDefaults.find((d) => d.kpi === id);
  return fallback === undefined ? null : { limit: fallback.limit, cmp: fallback.cmp, isDefault: true };
}

export function headline(team: TeamState, market: MarketView): Headline {
  const me = meBank(team, market);
  const ranking = team.cohort.ranking;
  // A tie prints the rank the tied banks share (the best of them), never one bank's own place within it ("Tied 4th" and "Tied 5th" for the same two banks).
  const tiedIds = new Set(me?.score?.tiedWithTeamIds ?? []);
  const placed = me === undefined ? "–" : displayRank(me);
  const shared = me?.rank == null ? null : me.score?.sharedRank !== undefined && typeof placed === "number" ? placed : Math.min(me.rank, ...market.banks.flatMap((b) => (tiedIds.has(b.teamId) && b.rank !== null ? [b.rank] : [])));
  return {
    rank: shared,
    of: market.banks.length,
    tied: (me?.score?.tiedWithTeamIds.length ?? 0) > 0,
    tiedWith: market.banks.filter((b) => tiedIds.has(b.teamId)).map((b) => b.name),
    gated: me?.gateRankedLast === true,
    gateText: me?.score?.gate?.text,
    ...(ranking.mode === "weightedBlend"
      ? { blendTooltip: `Blend: value ${ranking.weights.value}% · customers ${ranking.weights.customers}% · employees ${ranking.weights.employees}% · regulators ${ranking.weights.regulators}%. Score: $/share vs your twin.` }
      : {}),
  };
}


/** COE premia, non-zero, largest first; the total is the sum in bp. */
/**
 * The capital premium names the capital record once, as the valuation reads it (10.2 E1′), against the market's own line: with `record` (that line and the record) the part reads
 * "your capital record, 9.84% against the market's 10.00% line". The plain average CET1 of the risk memo is a different figure and is not printed here.
 */
export function premiaLine(premia: Premia, record?: { readonly lineRate: number; readonly recordRate: number }): { readonly totalBp: number; readonly parts: readonly { readonly label: string; readonly bp: number }[]; readonly recordNote?: string } {
  const labelOf = (key: keyof Premia): string => key === "capital" && record !== undefined ? `your capital record, ${formatKpi("cet1Reported", record.recordRate)} against the market’s ${formatKpi("cet1Reported", record.lineRate)} line` : PREMIA[key].label;
  const parts = PREMIUM_KEYS
    .map((key) => ({ label: labelOf(key), bp: Math.round(premia[key]) }))
    .filter((p) => p.bp !== 0)
    .sort((a, b) => b.bp - a.bp);
  const recordNote = record !== undefined && parts.some((p) => p.label === labelOf("capital")) ? `Your capital record is ${CAPITAL_RECORD_COUNTS}, averaged over every quarter-end.` : undefined;
  return { totalBp: parts.reduce((sum, p) => sum + p.bp, 0), parts, ...(recordNote === undefined ? {} : { recordNote }) };
}

/** The premia as a sentence in basis points, the cockpit's unit for a rate move: "The market wants 28bp more return from you: 20bp for rate risk, 8bp for uninsured deposits." */
export function premiaSentence(line: ReturnType<typeof premiaLine>): string {
  return `The market wants ${formatBp(line.totalBp)} more return from you: ${line.parts.map((p) => `${formatBp(p.bp)} for ${p.label}`).join(", ")}.${line.recordNote === undefined ? "" : ` ${line.recordNote}`}`;
}

export type LimitStatus = "ok" | "watch" | "breach" | "inherited";

export interface LimitRow {
  readonly kpi: KpiId;
  readonly label: string;
  readonly value: number;
  readonly limitText: string;
  /** True when the team saved no limits and this row is the game's default for the measure. */
  readonly isDefault?: boolean;
  readonly status: LimitStatus;
  /** Bar fill and the limit tick, as % of the meter; the tick sits at 80%. */
  readonly fillPct: number;
  readonly tickPct: number;
}

const TICK_PCT = 80;
const WATCH_USE = 0.9;

/**
 * One row per risk-appetite limit against the latest released KPIs. `use` is how much of the limit is used: 1 = at the limit.
 * A limit already breached at SimStart is "inherited" (warn) until the first release; after that a breach is critical.
 */
export function limitRows(team: TeamState, catalog?: ContentCatalog): LimitRow[] {
  const now = latestKpis(team);
  const start = team.kpiHistory[0]?.kpis ?? {};
  const released = team.releasedRounds.length > 0;
  // A team that saved no limits is still held to the game's defaults, so they are listed (and flagged) rather than "no limits".
  const isDefault = !limitsSaved(team) && catalog !== undefined;
  const source = isDefault ? (catalog?.riskAppetiteDefaults ?? []) : (team.strategyStatement?.riskAppetite ?? []);
  return source.flatMap((l) => {
    const value = now[l.kpi];
    if (value === undefined) return [];
    const breaches = (v: number) => (l.cmp === "le" ? printedKpi(l.kpi, v) > printedKpi(l.kpi, l.limit) : printedKpi(l.kpi, v) < printedKpi(l.kpi, l.limit));
    const use = l.cmp === "le" || l.limit < 0 ? value / l.limit : l.limit / value;
    const startValue = start[l.kpi];
    const status: LimitStatus = breaches(value)
      ? (!released && startValue !== undefined && breaches(startValue) ? "inherited" : "breach")
      : use >= WATCH_USE ? "watch" : "ok";
    const bound = l.limit < 0 ? "no worse than" : l.cmp === "le" ? "max" : "floor";
    return [{ kpi: l.kpi, label: kpiLabel(l.kpi), value, limitText: `${bound} ${formatKpi(l.kpi, l.limit)}`, ...(isDefault ? { isDefault } : {}), status, fillPct: Math.min(Math.max(use, 0), 1.25) * TICK_PCT, tickPct: TICK_PCT }];
  });
}

export interface CapitalPoint { readonly label: string; readonly reported: number; readonly marked: number }

/** SimStart plus every released quarter-end, last eight. No projection: only released actuals. */
export function capitalSeries(team: TeamState, catalog: ContentCatalog, reports: readonly RoundReport[]): CapitalPoint[] {
  const start = team.kpiHistory[0]?.kpis ?? {};
  const points: CapitalPoint[] = [];
  if (start.cet1Reported !== undefined && start.cet1Marked !== undefined) points.push({ label: "Start", reported: start.cet1Reported, marked: start.cet1Marked });
  for (const report of reports) {
    for (const q of report.quarterly) {
      const { cet1Reported, cet1Marked } = q.kpis;
      if (cet1Reported !== undefined && cet1Marked !== undefined) points.push({ label: quarterLabel(catalog, q.periodEndMonth), reported: cet1Reported, marked: cet1Marked });
    }
  }
  return points.slice(-8);
}

/** Gap in bp between reported and marked CET1 (reported − marked) × 10,000. */
/** The gap between the two printed levels (11.52% less 10.62% is 90bp), so the figure subtracts on the page. */
export const gapBp = (reported: number, marked: number): number => Math.round(printedKpi("cet1Reported", reported) * 10_000) - Math.round(printedKpi("cet1Marked", marked) * 10_000);

/** Quarter-end gap widening: ≥ +10bp vs the prior point is the warn chip (GxDualCapital). */
export const GAP_WIDENING_BP = 10;

export function gapWidening(points: readonly CapitalPoint[]): number | null {
  const prior = points[points.length - 2];
  const last = points[points.length - 1];
  return prior === undefined || last === undefined ? null : gapBp(last.reported, last.marked) - gapBp(prior.reported, prior.marked);
}

/** "What changed" for the brief: fed funds and 10y between the last two published environments, in bp. */
export function envChange(market: MarketView): { readonly ffRate: number; readonly ffDeltaBp: number | null; readonly tenYearRate: number; readonly tenYearDeltaBp: number | null } | null {
  const last = market.env[market.env.length - 1];
  if (last === undefined) return null;
  const prior = market.env[market.env.length - 2];
  return {
    ffRate: last.ffRate, ffDeltaBp: prior === undefined ? null : rateDeltaBp(last.ffRate, prior.ffRate),
    tenYearRate: last.curveRate["10y"], tenYearDeltaBp: prior === undefined ? null : rateDeltaBp(last.curveRate["10y"], prior.curveRate["10y"]),
  };
}

/** content §2.9: the outlook line, shown when the outlook pauses or resumes at the latest quarter-end. */
export function outlookLine(market: MarketView): string | null {
  const outlook = market.outlook;
  const q = outlook?.quarters;
  if (outlook === undefined || q === undefined || q.length === 0) return null;
  const before = q[q.length - 2]?.paused ?? false;
  if (outlook.pausedNow && !before) return "The outlook has turned: unemployment is rising and the consensus sees more. Cautious banks have paused buybacks.";
  if (!outlook.pausedNow && before) return "The outlook has steadied; banks that paused have resumed buybacks.";
  return null;
}

/** The change in a KPI from SimStart to now, as the difference of the two printed levels in the KPI's own change unit ("+2.9 pts", "+30bp"); null before a second point exists. */
export function sinceStart(team: TeamState, id: KpiId): { readonly text: string; readonly value: number } | null {
  const series = kpiSeries(team, id);
  const first = series[0];
  const last = series[series.length - 1];
  if (first === undefined || last === undefined || series.length < 2) return null;
  const diff = printedDifference(id, last, first);
  return { text: diff.text, value: diff.value };
}

export const bpText = (bp: number): string => formatBp(Math.abs(bp));

/** Composite rating words; "CAMELS" never appears in participant copy (the component letters are labels only). */
export const RATING_WORDS: Readonly<Record<number, string>> = { 1: "Strong", 2: "Satisfactory", 3: "Fair", 4: "Marginal", 5: "Unsatisfactory" };

export const SUPERVISORY_WORDS = { none: "No supervisory action", mou: "Under an agreement with the regulator (a memorandum of understanding)", consentOrder: "Consent order", forcedRecap: "Forced recapitalization" } as const;

const RANKS_BELOW = "ranks below every bank rated 1 or 2";

/**
 * When an action starts or ends (models 09.1 E6-E8, 09.2 E2): the composite is rated at each quarter-end, a 3 or worse starts the action at once
 * and ranks the bank below the rest, and an improvement takes hold only after consecutive better quarter-ends. The DTO carries no start month, so the line states the rule, not a date.
 */
export function supervisoryTiming(composite: number, status: keyof typeof SUPERVISORY_WORDS): string {
  if (status === "none") return composite >= 3 ? RANKS_BELOW : `a rating of 3 or worse at a quarter-end starts a supervisory action and ${RANKS_BELOW}`;
  return status === "mou" ? `${RANKS_BELOW}; lifts only after consecutive quarter-ends rated 2 or better` : RANKS_BELOW;
}

/** What a one-step change between two released points covers: always the year it closed, "over FY2026" (the opening is the base of the first year, so one span reads one way on every screen). */
export function changeSpanLabel(catalog: ContentCatalog, latestRoundNo: number): string {
  return `over ${fiscalYearLabel(catalog, latestRoundNo)}`;
}
